<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { mdiSpeedometer, mdiClockOutline } from '@mdi/js'
import { useUploadStore } from '../../stores/useUploadStore'

const store = useUploadStore()
const { t } = useI18n()

const isVisible = computed(() => {
  return store.activeUploads.length > 0 || store.pendingUploads.length > 0
})

const progress = computed(() => store.overallProgress.totalProgress)

const completedCount = computed(() => store.completedUploads.length)

const totalCount = computed(() => {
  return (
    store.activeUploads.length +
    store.pendingUploads.length +
    store.completedUploads.length
  )
})

const formattedSpeed = computed(() => {
  const speed = store.uploadSpeed
  if (speed === 0) return null

  const k = 1024
  const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s']
  const i = Math.floor(Math.log(speed) / Math.log(k))
  const value = parseFloat((speed / Math.pow(k, i)).toFixed(1))
  return `${value} ${sizes[i]}`
})

const formattedEta = computed(() => {
  const eta = store.uploadEta
  if (eta === null || eta === Infinity || eta <= 0) return null

  const totalSeconds = Math.ceil(eta)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
})
</script>

<template>
  <Transition name="fade-slide">
    <div v-if="isVisible" class="upload-progress-header">
      <div class="upload-progress-header__ring">
        <v-progress-circular
          :model-value="progress"
          :size="40"
          :width="3"
          color="primary"
        >
          <span class="upload-progress-header__percent">{{ progress }}</span>
        </v-progress-circular>
      </div>

      <div class="upload-progress-header__info">
        <span class="upload-progress-header__files">
          {{ completedCount }}/{{ totalCount }} {{ t('assets.upload.progress.files') }}
        </span>

        <div class="upload-progress-header__stats">
          <span v-if="formattedSpeed" class="upload-progress-header__stat">
            <v-icon :icon="mdiSpeedometer" size="12" />
            {{ formattedSpeed }}
          </span>

          <span v-if="formattedEta" class="upload-progress-header__stat">
            <v-icon :icon="mdiClockOutline" size="12" />
            {{ formattedEta }}
          </span>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.upload-progress-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin: 0 12px 8px;
  border-radius: 12px;

  // Glassmorphism style
  background: rgba(var(--v-theme-primary), 0.06);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(var(--v-theme-primary), 0.12);

  &__ring {
    flex-shrink: 0;
  }

  &__percent {
    font-size: 11px;
    font-weight: 600;
    color: rgb(var(--v-theme-primary));
  }

  &__info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__files {
    font-size: 13px;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), 0.87);
  }

  &__stats {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__stat {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: rgba(var(--v-theme-on-surface), 0.75);
  }
}

// Transition
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
